import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MainLayout from "../../../components/layout/MainLayout";
import { useMasterData } from "../../../hooks/useMasterData";
import { usePermissions } from "../../../contexts/PermissionContext";
import { feesApi } from "../api/feesApi";

type ReceiptRow = {
  id: string;
  receiptNo: string;
  status?: string;
  studentId: string;
  totalPaid: number;
  paymentDate: string;
  remarks?: string;
  paymentMode?: {
    id?: string;
    name?: string;
  };
  student?: {
    firstName?: string;
    lastName?: string;
    scholarNumber?: string;
    class?: { name?: string };
    section?: { name?: string };
  };
};

const PAYMENT_MODE_OPTIONS = ["CASH", "CHEQUE", "ONLINE", "CARD", "UPI"];
const STATUS_OPTIONS = ["CONFIRMED", "PROVISIONAL", "CANCELLED"];

const FeeReceiptManagementPage: React.FC = () => {
  const { masters, fetchAcademicSessions, fetchMasters, fetchSections } = useMasterData();
  const { hasAnyPermission } = usePermissions();

  const [sessionId, setSessionId] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [sections, setSections] = useState<Array<{ id: string; name: string }>>([]);
  const [receiptNo, setReceiptNo] = useState("");
  const [search, setSearch] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [status, setStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [rows, setRows] = useState<ReceiptRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewData, setViewData] = useState<any>(null);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelId, setCancelId] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);

  const canView = useMemo(
    () => hasAnyPermission(["FEES_VIEW", "FEE_VIEW", "FEE_MANAGE", "FEES_MANAGE"]),
    [hasAnyPermission],
  );
  const canManage = useMemo(
    () => hasAnyPermission(["FEES_MANAGE", "FEE_MANAGE"]),
    [hasAnyPermission],
  );

  useEffect(() => {
    fetchMasters();
    fetchAcademicSessions();
  }, [fetchMasters, fetchAcademicSessions]);

  useEffect(() => {
    const active = masters.academicSessions.find((s) => s.isActive);
    if (active && !sessionId) {
      setSessionId(active.id);
    }
  }, [masters.academicSessions, sessionId]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!classId) {
        setSections([]);
        setSectionId("");
        return;
      }
      const res = await fetchSections(classId);
      if (!cancelled) {
        setSections(Array.isArray(res) ? res : []);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [classId, fetchSections]);

  const load = async () => {
    if (!canView) return;

    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const data = await feesApi.receipts({
        sessionId: sessionId || undefined,
        classId: classId || undefined,
        sectionId: sectionId || undefined,
        receiptNo: receiptNo || undefined,
        search: search || undefined,
        paymentMode: paymentMode || undefined,
        status: status || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      });

      const list = Array.isArray(data) ? data : [];
      setRows(list);
      if (!list.length) {
        setMessage("No receipts found for selected filters");
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to load receipts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId && canView) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, canView]);

  const statusColor = (value?: string): "success" | "warning" | "error" | "default" => {
    const normalized = String(value || "").toUpperCase();
    if (normalized === "CONFIRMED") return "success";
    if (normalized === "PROVISIONAL") return "warning";
    if (normalized === "CANCELLED") return "error";
    return "default";
  };

  const openReceiptPdf = async (receiptId: string) => {
    try {
      const file = await feesApi.getReceiptPdfBlob(receiptId);
      const url = URL.createObjectURL(file.blob);
      const win = window.open(url, "_blank");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.download = file.filename;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to open receipt PDF");
    }
  };

  const handleView = async (receiptId: string) => {
    setViewOpen(true);
    setViewLoading(true);
    setViewData(null);
    setError(null);
    try {
      const detail = await feesApi.receiptById(receiptId);
      setViewData(detail);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to load receipt details");
      setViewOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const handleReprint = async (receiptId: string) => {
    try {
      const result = await feesApi.reprintReceipt(receiptId);
      await openReceiptPdf(receiptId);
      setMessage(`Receipt reprinted. Reprint count: ${result?.reprintCount ?? 0}`);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to reprint receipt");
    }
  };

  const askCancel = (receiptId: string) => {
    setCancelId(receiptId);
    setCancelReason("");
    setCancelOpen(true);
  };

  const doCancel = async () => {
    if (!cancelId) return;
    setCancelLoading(true);
    setError(null);

    try {
      await feesApi.cancelReceipt(cancelId, cancelReason || undefined);
      setMessage("Receipt cancelled successfully");
      setCancelOpen(false);
      setCancelId("");
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to cancel receipt");
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
          Fee Receipt Management
        </Typography>

        {!canView && (
          <Alert severity="error" sx={{ mb: 2 }}>
            You do not have permission to view fee receipts.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>
            {message}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Session"
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
              >
                <MenuItem value="">All Sessions</MenuItem>
                {masters.academicSessions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label="Class" value={classId} onChange={(e) => setClassId(e.target.value)}>
                <MenuItem value="">All Classes</MenuItem>
                {masters.classes.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Section"
                value={sectionId}
                onChange={(e) => setSectionId(e.target.value)}
                disabled={!classId}
              >
                <MenuItem value="">All Sections</MenuItem>
                {sections.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Receipt No"
                value={receiptNo}
                onChange={(e) => setReceiptNo(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search Student"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name or Scholar No"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Payment Mode"
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {PAYMENT_MODE_OPTIONS.map((mode) => (
                  <MenuItem key={mode} value={mode}>
                    {mode}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {STATUS_OPTIONS.map((st) => (
                  <MenuItem key={st} value={st}>
                    {st}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="From"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="To"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={9}>
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ height: "100%", alignItems: "center" }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setClassId("");
                    setSectionId("");
                    setReceiptNo("");
                    setSearch("");
                    setPaymentMode("");
                    setStatus("");
                    setFromDate("");
                    setToDate("");
                  }}
                >
                  Reset
                </Button>
                <Button variant="contained" onClick={load} disabled={loading || !canView}>
                  {loading ? "Loading..." : "Search"}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Receipt No</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Scholar No</TableCell>
                <TableCell>Class / Section</TableCell>
                <TableCell>Payment Mode</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const studentName = `${row.student?.firstName || ""} ${row.student?.lastName || ""}`.trim() || "-";
                const classSection = `${row.student?.class?.name || "-"} / ${row.student?.section?.name || "-"}`;
                const isCancelled = String(row.status || "").toUpperCase() === "CANCELLED";

                return (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.receiptNo || "-"}</TableCell>
                    <TableCell>{row.paymentDate ? new Date(row.paymentDate).toLocaleDateString() : "-"}</TableCell>
                    <TableCell>{studentName}</TableCell>
                    <TableCell>{row.student?.scholarNumber || "-"}</TableCell>
                    <TableCell>{classSection}</TableCell>
                    <TableCell>{row.paymentMode?.name || "-"}</TableCell>
                    <TableCell align="right">{Number(row.totalPaid || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip size="small" color={statusColor(row.status)} label={row.status || "UNKNOWN"} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="outlined" onClick={() => handleView(row.id)}>
                          View
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => openReceiptPdf(row.id)}>
                          Download
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => handleReprint(row.id)}>
                          Reprint
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          disabled={!canManage || isCancelled}
                          onClick={() => askCancel(row.id)}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    {loading ? "Loading receipts..." : "No receipts"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Receipt Details</DialogTitle>
        <DialogContent dividers>
          {viewLoading && <Typography>Loading...</Typography>}
          {!viewLoading && viewData && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2"><strong>Receipt No:</strong> {viewData.receiptNo || "-"}</Typography>
                <Typography variant="body2"><strong>Status:</strong> {viewData.status || "-"}</Typography>
                <Typography variant="body2"><strong>Date:</strong> {viewData.paymentDate ? new Date(viewData.paymentDate).toLocaleDateString() : "-"}</Typography>
                <Typography variant="body2"><strong>Payment Mode:</strong> {viewData.paymentMode?.name || "-"}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2"><strong>Student:</strong> {`${viewData.student?.firstName || ""} ${viewData.student?.lastName || ""}`.trim() || "-"}</Typography>
                <Typography variant="body2"><strong>Scholar No:</strong> {viewData.student?.scholarNumber || "-"}</Typography>
                <Typography variant="body2"><strong>Class:</strong> {viewData.student?.class?.name || "-"}</Typography>
                <Typography variant="body2"><strong>Section:</strong> {viewData.student?.section?.name || "-"}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Receipt Lines</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fee Head</TableCell>
                      <TableCell>Installment</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(viewData.lines || []).map((line: any) => (
                      <TableRow key={line.id}>
                        <TableCell>{line.feeHead?.name || "-"}</TableCell>
                        <TableCell>{line.feeInstallment?.name || "-"}</TableCell>
                        <TableCell align="right">{Number(line.amount || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                    {!(viewData.lines || []).length && (
                      <TableRow>
                        <TableCell colSpan={3} align="center">No line items</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Cancel Receipt</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Reason"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Optional cancellation reason"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Close</Button>
          <Button color="error" variant="contained" onClick={doCancel} disabled={cancelLoading}>
            {cancelLoading ? "Cancelling..." : "Confirm Cancel"}
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
};

export default FeeReceiptManagementPage;
